// Copyright (c) Move Closer 2022.

import { Component, Mixins } from 'vue-property-decorator'
import { Subscription } from 'rxjs'
import {
  AnyObject,
  Authentication,
  AuthEvent,
  AuthEventType,
  AuthServiceType,
  EventbusType,
  IEventbus,
  IWindow,
  ResourceActionFailed,
  WindowType
} from '@movecloser/front-core'

import { ConnectorErrors, Inject, logger } from '../../../../support'

import { DrawerType, IDrawer } from '../../../shared/contracts/services'

import { IProfileService, ProfileServiceType } from '../../../profile/contracts'
import { UserMixin } from '../../../profile/shared'

import { AuthControlServiceType, IAuthControl } from '../../contracts'
import { AuthMixin, openAuthDrawer } from '../index'
import { ISessionService, SessionServiceType } from '../../services'
import { RouteNames } from '../../routes'
import { UserModel } from '../contracts'

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
@Component<ListenToAuthMixin>({
  name: 'ListenToAuthMixin',
  created (): void {
    this.listenToAuthService()
  },
  destroyed (): void {
    this.subscription.unsubscribe()
  }
})
export class ListenToAuthMixin extends Mixins(AuthMixin, UserMixin) {
  @Inject(AuthServiceType)
  protected readonly authService!: Authentication<UserModel>

  @Inject(AuthControlServiceType)
  protected readonly authControl!: IAuthControl

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  @Inject(DrawerType)
  protected readonly drawer!: IDrawer

  @Inject(ProfileServiceType)
  protected readonly profileService!: IProfileService

  @Inject(SessionServiceType)
  protected readonly session!: ISessionService

  @Inject(WindowType)
  protected readonly windowService!: IWindow

  /**
   * Determines the RxJs subscription instance.
   */
  protected subscription!: Subscription

  /**
   * Validate unauthorized user.
   */
  protected handleInvalidUser () {
    this.$store.commit('auth/SET_IS_WAITING_FOR_AUTH', true)
    this.$store.commit('auth/SET_IS_USER_LOGGED_IN', false)
    this.$store.commit('auth/SET_IS_USER_PREMIUM', false)
    this.$store.commit('auth/SET_IS_USER_B2B', false)
    this.setUser(null)
    this.session.deleteAuthCookie()

    // FIXME: Route from config.
    if (this.$route.meta && this.$route.meta.requiresAuth && this.$route.name !== 'auth.auth') {
      this.$router.push({ name: 'auth.auth' }).then(() => {
        this.$store.commit('auth/SET_IS_WAITING_FOR_AUTH', false)
      })
    }
    this.$nextTick(() => {
      this.$store.commit('auth/SET_IS_WAITING_FOR_AUTH', false)
    })
  }

  /**
   * Listens to the events in the `Authorization` and reacts to them.
   */
  protected listenToAuthService (): void {
    if (this.windowService.isServer) {
      return
    }

    this.subscription = this.authService.listen((event: AuthEvent) => {
      logger(event.type, 'debug')

      switch (event.type) {
        case AuthEventType.Booted: {
          this.handleInvalidUser()
          this.eventBus.emit('app:authentication', 'booted')
          break
        }
        case AuthEventType.BootedWithToken:
        case AuthEventType.Refresh: {
          this.$store.commit('root/setLoaderOn')
          this.$store.commit('auth/SET_IS_WAITING_FOR_AUTH', true)
          this.eventBus.emit('app:authentication', 'refreshing')

          if (this.windowService.native && event.token) {
            // TODO: Consider removing this step is API won't provide check operation.
            this.authControl.checkToken(event.token.token.accessToken)
              .then(() => {
                if (event.token) {
                  this.authService.setToken(event.token.token)
                  this.session.setAuthCookie(event.token.token.accessToken)
                }
              })
              .catch(() => {
                this.authService.deleteToken()
                this.session.deleteAuthCookie()
              })
              .finally(() => {
                this.eventBus.emit('app:authentication', 'refreshed')
              })
          }
          break
        }
        case AuthEventType.Authenticated: {
          if (this.authService.token) {
            this.session.setAuthCookie(this.authService.token.accessToken)
            this.profileService.getUserDetails(this.authService.token.accessToken)
              .then(user => {
                this.setUser(user)
                this.$store.commit('auth/SET_IS_USER_LOGGED_IN', this.authService.check())
                this.authService.setUser(user)
                this.$store.commit('auth/SET_IS_USER_PREMIUM', this.authService.user?.isPremium)
                this.$store.commit('auth/SET_IS_USER_B2B', this.authService.user?.isB2B)
              })
              .catch((e: ResourceActionFailed) => {
                if (e.status === 401 || e.status === ConnectorErrors.Unauthenticated) {
                  this.authService.deleteToken()
                  this.session.deleteAuthCookie()
                  return
                }

                logger(e, 'error')
              })
              .finally(() => {
                this.$store.commit('auth/SET_IS_USER_LOGGED_IN', this.authService.check())
                this.$store.commit('auth/SET_IS_USER_PREMIUM', this.authService.user?.isPremium)
                this.$store.commit('auth/SET_IS_USER_B2B', this.authService.user?.isB2B)

                this.eventBus.emit('app:authentication', 'booted')

                setTimeout(() => {
                  this.$store.commit('auth/SET_IS_WAITING_FOR_AUTH', false)
                  this.$store.commit('root/setLoaderOff')
                }, 250)
              })
          }
          break
        }
        case AuthEventType.Invalidated: {
          this.handleInvalidUser()
          this.eventBus.emit('app:authentication', 'booted')
          break
        }
      }
    })
  }

  /**
   * Triggers drawer to login user.
   */
  protected requireAuthentication (): void {
    const componentsConfig = this.$configuration?.byKey<AnyObject>('components')

    if (!componentsConfig) {
      openAuthDrawer(
        this.drawer,
        { onClose: this.onDrawerClose },
        { closeOnClickOutside: false }
      )
      return
    }

    const strategy: string = componentsConfig.structure.authActionStrategy

    switch (strategy) {
      case 'router':
        this.$router.push({ name: `auth.${RouteNames.Auth}` })
        break
      case 'drawer':
      default:
        openAuthDrawer(this.drawer, { onClose: this.onDrawerClose }, { closeOnClickOutside: false })
        break
    }
  }

  /**
   * Handle drawer close.
   */
  private async onDrawerClose (result: boolean): Promise<void> {
    if (result) {
      return
    }

    if (this.$route.fullPath === '/') {
      return
    }

    await this.$router.push('/')
  }
}

export default ListenToAuthMixin
